import { useState, createContext, useCallback, useMemo } from 'react';

export const TagContext = createContext();

export const TagContextProvider = ({ children }) => {
  const [continuationTag, setContinuationTag] = useState(null);
  const [highlightedOption, setHighlightedOption] = useState('tagger');
  const [tagDataMap, setTagDataMap] = useState({});
  const [draftTag, setDraftTag] = useState(null);
  const [clickedTagIds, setClickedTagIds] = useState(null);

  const [tagLanguage, setTagLanguageState] = useState(() => localStorage.getItem('tagLanguage') || 'sv');

  const setTagLanguage = useCallback((lang) => {
    setTagLanguageState(lang);
    localStorage.setItem('tagLanguage', lang);
  }, []);

  const [visibilityOption, setVisibilityOption] = useState(['core']);
  const [extensionConceptIds, setExtensionConceptIds] = useState(() => new Set());
  const [tableSelection, setTableSelection] = useState(null);
  const [blockSelection, setBlockSelectionState] = useState(null);
  const [blockScope, setBlockScope] = useState('cell');

  const [termHighlightTagIds, setTermHighlightTagIds] = useState(() => new Set());
  const [calcPickMode, setCalcPickMode] = useState(null);
  const [selectedExcludePath, setSelectedExcludePath] = useState(null);
  const [selectedFootnoteId, setSelectedFootnoteId] = useState(null);

  const setTableTagSelection = useCallback((selection) => {
    setTableSelection(selection);
    setBlockSelectionState(null);
  }, []);

  const clearTableTagSelection = useCallback(() => {
    setTableSelection(null);
  }, []);

  const setBlockSelection = useCallback((selection) => {
    setBlockSelectionState(selection);
    setTableSelection(null);
    const proseKind = selection?.kind === 'paragraph' || selection?.kind === 'list';
    setBlockScope(proseKind ? 'text' : 'cell');
  }, []);

  const clearBlockSelection = useCallback(() => {
    setBlockSelectionState(null);
    setBlockScope('cell');
  }, []);

  const actions = useMemo(
    () => ({
      setContinuationTag,
      setHighlightedOption,
      setTagDataMap,
      setDraftTag,
      setClickedTagIds,
      setTableTagSelection,
      clearTableTagSelection,
      setTagLanguage,
      setVisibilityOption,
      setExtensionConceptIds,
      setBlockSelection,
      clearBlockSelection,
      setSelectedExcludePath,
      setSelectedFootnoteId,
      setBlockScope,
      setTermHighlightTagIds,
      setCalcPickMode,
    }),
    [setTableTagSelection, clearTableTagSelection, setTagLanguage, setBlockSelection, clearBlockSelection]
  );

  const value = useMemo(
    () => ({
      continuationTag,
      highlightedOption,
      tagDataMap,
      tableSelection,
      draftTag,
      clickedTagIds,
      tagLanguage,
      visibilityOption,
      extensionConceptIds,
      blockSelection,
      blockScope,
      termHighlightTagIds,
      calcPickMode,
      selectedExcludePath,
      selectedFootnoteId,
      ...actions,
    }),
    [
      continuationTag,
      highlightedOption,
      tagDataMap,
      tableSelection,
      draftTag,
      clickedTagIds,
      tagLanguage,
      visibilityOption,
      extensionConceptIds,
      blockSelection,
      blockScope,
      termHighlightTagIds,
      calcPickMode,
      selectedExcludePath,
      selectedFootnoteId,
      actions,
    ]
  );

  return <TagContext.Provider value={value}>{children}</TagContext.Provider>;
};
